<template>
  <!-- Breadcrumb Navigation -->
  <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
    <router-link :to="{ name: 'pages.admin.dashboard' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <icon icon="solar:home-smile-bold-duotone" /> 
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.products") }}</span>
    </button>
  </div>

  <!-- Main Content -->
  <div class="tw-p-2">
    <div class="tw-h-full tw-w-full tw-p-4x tw-bg-whitex tw-rounded tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
      
      <!-- Header Section -->
      <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <div class="tw-flex tw-items-center tw-gap-4">
          <!-- Search Field -->
          <FormField 
            v-model="search" 
            type="text" 
            :hide-label="true" 
            :hide-errors="true" 
            :placeholder="$t('common.products-search-placeholder', 'Search products...')"
            left-icon="solar:magnifer-linear" 
            @input="onSearch" 
            class="tw-bg-white tw-rounded-md tw-min-w-[300px]" 
          />

          <!-- Category Filter -->
          <FormSelect 
            v-model="filters.category" 
            :options="categoryOptions"
            :placeholder="$t('common.filter-by-category', 'Filter by category')"
            left-icon="solar:filter-bold-duotone" 
            class="tw-bg-white tw-rounded-md tw-min-w-[200px]"
          />

          <!-- Company Filter -->
          <FormSelect 
            v-model="filters.company" 
            :options="companyOptions"
            :placeholder="$t('common.filter-by-company', 'Filter by company')"
            left-icon="solar:buildings-bold-duotone" 
            class="tw-bg-white tw-rounded-md tw-min-w-[200px]"
          />
        </div>

        <!-- Create Product Button -->
        <div>
          <a-button 
            as="router-link" 
            :to="{ name: 'pages.admin.products.create' }" 
            intent="flat" 
            left-icon="solar:add-circle-bold-duotone"
            class="tw-min-w-[160px]"
          >
            {{ $t('common.create-new-product', 'Create New Product') }}
          </a-button>
        </div>
      </div>
      
      <!-- Products Table -->
      <AdminProductTable 
        :loading="loading" 
        :items="products" 
        :options="paginationOptions" 
        @next="onNext"
        @previous="onPrevious" 
        @edit="onEdit" 
        @delete="onDelete" 
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import AdminProductTable from '@/components/dashboard/admin/products/table/AdminProductTable.vue';
import productApi from '@/api/products';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const products = ref([]);
const search = ref('');

// Pagination options
const paginationOptions = ref({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null
});

// Filters
const filters = reactive({
    category: '',
    company: ''
});

// Filter options
const categoryOptions = ref([
    { id: '', name: t('common.all') }
]);

const companyOptions = ref([
    { id: '', name: t('common.all') }
]);

// Search debounce
let searchTimeout = null;
let filterTimeout = null;

// Watch for filter changes
watch(() => filters.category, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchProducts(1);
    }, 300);
}, { immediate: false });

watch(() => filters.company, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchProducts(1);
    }, 300);
}, { immediate: false });

// Methods
const fetchProducts = async (page = 1) => {
    loading.value = true;

    try {
        const params = {
            page,
            per_page: paginationOptions.value.per_page,
            search: search.value,
            ...filters
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        const response = await productApi.admin.paginate(params);
        
        if (response.data && response.data.data) {
            // Extract the pagination object from response.data.data
            const paginationData = response.data.data;
            
            // Extract the actual products array from response.data.data.data
            if (paginationData.data && Array.isArray(paginationData.data)) {
                products.value = paginationData.data;
            } else {
                console.error('Expected products array but got:', typeof paginationData.data, paginationData.data);
                products.value = [];
            }
            
            // Set pagination options from the pagination data
            paginationOptions.value = {
                current_page: paginationData.current_page || 1,
                per_page: paginationData.per_page || 10,
                total: paginationData.total || 0,
                last_page: paginationData.last_page || 1,
                next_page_url: paginationData.next_page_url || null,
                prev_page_url: paginationData.prev_page_url || null
            };
        } else {
            console.error('Invalid response structure:', response);
            products.value = [];
        }
    } catch (error) {
        console.error('Error fetching products:', error);
        console.error('Error details:', error.response);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchProducts(1);
    }, 500);
};

const onNext = () => {
    if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
        fetchProducts(paginationOptions.value.current_page + 1);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.current_page > 1) {
        fetchProducts(paginationOptions.value.current_page - 1);
    }
};

const onEdit = (product) => {
    // Edit is handled by the action button routing
    console.log('Edit product:', product.id);
};

const onDelete = (product) => {
    // Delete functionality can be implemented here if needed
    console.log('Delete product:', product.id);
};

// Fetch filter options (categories and companies)
const fetchFilterOptions = async () => {
    try {
        // You can implement these API calls if they exist
        // For now, we'll keep the basic "All" option
        categoryOptions.value = [
            { id: '', name: t('common.all') }
        ];
        
        companyOptions.value = [
            { id: '', name: t('common.all') }
        ];
    } catch (error) {
        console.error('Error fetching filter options:', error);
    }
};

// Initialize
onMounted(() => {
    fetchProducts();
    fetchFilterOptions();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>